/**
 * Session Expired Modal Component
 * Shows a user-friendly message when session expires or authentication fails
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface SessionExpiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  message?: string;
}

const COUNTDOWN_SECONDS = 10;

const SessionExpiredModal: React.FC<SessionExpiredModalProps> = ({ 
  isOpen, 
  onClose,
  message = "Your session has expired for security reasons. Please log in again to continue." 
}) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleLogin = useCallback(() => {
    // Clear timers
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }
    
    onClose();
    // Small delay to ensure modal closes smoothly
    setTimeout(() => {
      navigate('/login');
    }, 100);
  }, [navigate, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Start countdown when modal opens
  useEffect(() => {
    if (!isOpen) {
      // Reset countdown when modal closes
      setCountdown(COUNTDOWN_SECONDS);
      return;
    }

    // Reset countdown when modal opens
    setCountdown(COUNTDOWN_SECONDS);

    // Start countdown timer
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Countdown finished, will be handled by redirect timeout
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Auto-redirect after countdown
    redirectTimeoutRef.current = setTimeout(() => {
      handleLogin();
    }, COUNTDOWN_SECONDS * 1000);

    // Cleanup
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [isOpen, handleLogin]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-in fade-in slide-in-from-bottom-4">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-semibold text-gray-900 text-center mb-3">
          Session Expired
        </h2>

        {/* Message */}
        <p className="text-gray-600 text-center mb-4 leading-relaxed">
          {message}
        </p>

        {/* Countdown Timer */}
        <div className="text-center mb-6">
          <p className="text-sm text-gray-500">
            Redirecting to login in{' '}
            <span className="font-semibold text-blue-600">{countdown}</span>
            {' '}second{countdown !== 1 ? 's' : ''}...
          </p>
        </div>

        {/* Action Button */}
        <div className="flex justify-center">
          <button
            onClick={handleLogin}
            className="px-6 py-3 bg-black text-white font-medium rounded-lg hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 min-w-[140px]"
          >
            Go to Login Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionExpiredModal;

